import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { EEMonitoringProgress } from "../../../../../Component/Model/Dto/EmploymentEquity/Plan/EEMonitoringProgress";
import { yupResolver } from "@hookform/resolvers/yup";
import { monitoringProgressSchema } from "./MonitoringProgressValidation";
import { toast } from "react-toastify";
import { Box, Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { CustomDialogTitle } from "../../../../Common/Custom/CustomDialogTitle";
import { MDBox } from "../../../../../Component/Common/Components/MDBox";
import { AppQuill } from "../../../../../Component/Common/Components/FormValidationInputs/AppQuill";
import { NotesTable } from "../../../../Common/Notes/NotesTable";
import { LoadingButton } from "@mui/lab";
import { MonitoringProgressService } from "../../../../../Component/Api/EmploymentEquity/Plan/MonitoringProgress/MonitoringProgressService";
import { UserActivityService } from "../../../../../Component/Api/UserActivity/UserActivityService";
import { MonitoringProgressSection } from "../../../../../Component/Common/Constants/UserActivityTrackingSections";
import { UserActivityActionType } from "../../../../../Component/Model/Enum/BEE123Enums";

interface IProps {
    id?: string;
    scorecardId: string;
    link: string;
    previewPopup: boolean;
    handleCancel: () => void;
    refreshTable: () => void;
    setMonitoringProgressId: (id: string) => void;
}

export const MonitoringProgressModal = (props: IProps) => {
    
    const [monitoringProgressId, setMonitoringProgressId] = useState<string | undefined>(props.id);

    const stakeholderMaxLength: number = 100;
    const responsibilityMaxLength: number = 600;
    const frequencyMaxLength: number = 600;

    const scorecardElement = 22;
    const noteCaptureSheet = 38;

    const { control, reset, handleSubmit, formState: { isSubmitting }, setValue } = useForm<EEMonitoringProgress.DtoOut>({
        mode: 'all',
        defaultValues: {
            scorecardId: props.scorecardId,
        },
        resolver: yupResolver(monitoringProgressSchema)
    });

    const monitoringProgressLog = (actionType: number, actionDescription: string) => {
        UserActivityService.createActivityLog({
            section: MonitoringProgressSection,
            actionType: actionType,
            actionDescription: actionDescription,
            scorecardId: props.scorecardId,
            requestUrl: window.location.href
        });
    }

    const handleDataSubmit: SubmitHandler<EEMonitoringProgress.DtoOut> = async (monitoringProgress: EEMonitoringProgress.DtoOut) => {      
        try {
            if (!props.id) {
                const result = await MonitoringProgressService.CreateMonitoringProgress(monitoringProgress);
                monitoringProgressLog(UserActivityActionType.Create, "Add a progress entry");
                toast.success("Monitoring progress added succesfully");
                const savedId = result.data.id;
                setMonitoringProgressId(savedId);
                setValue("id", savedId);
                props.setMonitoringProgressId(savedId);
            }

            else {
                await MonitoringProgressService.UpdateMonitoringProgress(monitoringProgress);
                monitoringProgressLog(UserActivityActionType.Update, "Edit a progress entry");
                toast.success("Monitoring progress saved succesfully")
            }

            props.refreshTable();

        } catch (error) {
            console.log(error)
        }

    }

    const handleCancel = () => {
        props.handleCancel();
        props.refreshTable();
    }

    const getMonitoringProgress = async (id: string) => {
        try {
            let result = await MonitoringProgressService.GetMonitoringProgress(id);
            var monitoringProgress = 
                {
                    id: result.data.id,
                    scorecardId: result.data.scorecardId,
                    stakeholder: result.data.stakeholder,
                    responsibility: result.data.responsibility,
                    frequency: result.data.frequency
                } as EEMonitoringProgress.DtoOut
            reset(monitoringProgress)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        if (monitoringProgressId) {
            getMonitoringProgress(monitoringProgressId);
        }

    }, []);

    return (
        <Box >
            <Dialog open={props.previewPopup} onClose={handleCancel} aria-labelledby="form-dialog-title" maxWidth='md' fullWidth>
                <CustomDialogTitle id="customized-dialog-title" onClose={handleCancel}>
                    {props.id !== undefined ? "Edit" : "Add"}
                </CustomDialogTitle>
                <DialogContent>
                    <Grid container justifyContent='center' sx={{ my: 2 }}>
                        <Grid item xs={10} lg={10}>
                            <Box component="form"  id='MonitoringProgressForm' onSubmit={handleSubmit(handleDataSubmit)}>
                                <MDBox>
                                    <MDBox mb={1}>
                                        <Grid item xs={12}>

                                            <MDBox mb={2} className="leftAlign">
                                                <AppQuill control={control} name='stakeholder' label='Stakeholder*' maxLength={stakeholderMaxLength} />
                                            </MDBox>

                                            <MDBox mb={2} className="leftAlign">
                                                <AppQuill control={control} name='responsibility' label='Role / Responsibility*' maxLength={responsibilityMaxLength} />
                                            </MDBox>

                                            <MDBox mb={2} className="leftAlign">
                                                <AppQuill control={control} name='frequency' label='Frequency*' maxLength={frequencyMaxLength} />
                                            </MDBox>

                                        </Grid>
                                    </MDBox>
                                </MDBox>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <LoadingButton variant='contained' color='primary' type='submit' form='MonitoringProgressForm' loading={isSubmitting}>
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>);
}